import { useState, useEffect } from "react";
import { Box, Typography, Checkbox, CircularProgress } from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import dayjs from "dayjs";

import style from "./laboratory.module.css";
import {
  useGetLaboratoryDetailsQuery,
  useUpdateLabDetailsMutation,
} from "../../services/api";
import { Button, LabelBox } from "../../components";
import { ConfirmationDialog } from "../../components/Dialog";
import { colors } from "../../theme/colors";

const LaboratoryDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [faxNumberSelected, setFaxNumberSelected] = useState(false);
  const [deliveryEmailSelected, setDeliveryEmailSelected] = useState(false);
  const [activateModalVisible, setActivateModalVisible] = useState(false);
  const [isActivating, setIsActivating] = useState(false);
  const [message, setMessage] = useState<LabelMessageType | null>(null);

  const {
    data: laboratoryDetails,
    isLoading,
    isFetching,
  } = useGetLaboratoryDetailsQuery(id ?? "");
  const [updateLaboratoryDetails] = useUpdateLabDetailsMutation();

  useEffect(() => {
    setFaxNumberSelected(laboratoryDetails?.address?.is_fax_delivery ?? false)
    setDeliveryEmailSelected(laboratoryDetails?.address?.is_mail_delivery ?? false);
  }, [laboratoryDetails]);

  const handleActivate = async () => {
    if (!laboratoryDetails?.id) {
      return;
    }
    try {
      setIsActivating(true);
      await updateLaboratoryDetails({
        is_active: true,
        id: laboratoryDetails.id,
        deactivate_reason: "",
      }).unwrap();
      setActivateModalVisible(false);
    } catch (error: any) {
      setActivateModalVisible(false);
      setMessage({
        type: "error",
        text: error?.message || error?.data?.detail || "Something went wrong!",
      });
    } finally {
      setIsActivating(false);
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        marginTop: "30px",
      }}
    >
      <Box
        sx={{
          width: "100%",
          border: `1px solid ${colors.grey}`,
          borderRadius: "4px",
          height: "100%",
        }}
      >
        {isLoading || isFetching ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              minHeight: "calc(100vh - 97px)",
            }}
          >
            <CircularProgress size={40} sx={{ marginLeft: "10px" }} />
          </Box>
        ) : (
          <Box sx={{ height: "100%", padding: "15px 40px" }}>
            <Typography className={style.laboratoryDetailHeading}>
              Laboratory Details
            </Typography>
            <Box>
              <Box className={style.detailsRow}>
                <Typography className={style.title}>Laboratory</Typography>
                <Typography className={style.value}>
                  {laboratoryDetails?.name}
                </Typography>
              </Box>
              <Box className={style.detailsRow}>
                <Typography className={style.title}>Address</Typography>
                <Typography className={style.value}>
                  {`${laboratoryDetails?.address?.unit_number ? laboratoryDetails?.address?.unit_number + " " : ""}${laboratoryDetails?.address?.street_number ?? ""} ${laboratoryDetails?.address?.street_name}, ${laboratoryDetails?.address?.suburb ?? ""
                    } ${laboratoryDetails?.address?.state ?? ""} ${laboratoryDetails?.address?.postcode ?? ""
                    }`}
                </Typography>
              </Box>
            </Box>
            <Typography className={style.laboratoryDetailHeading}>
              Contact Details
            </Typography>
            <Box>
              <Box className={style.detailsRow}>
                <Typography className={style.title}>
                  General Enquiries
                </Typography>
                <Typography className={style.value}>
                  {laboratoryDetails?.account_manager_mail}
                </Typography>
              </Box>
              <Box className={style.detailsRow}>
                <Typography className={style.title}>
                  Additional Contacts
                </Typography>
                <Typography className={style.value} sx={{ display: "flex" }}>
                  {"For department-specific contacts, please refer to the"}
                  <Typography
                    component="span"
                    onClick={() => {
                      navigate("/users", {
                        state: { selectedTab: "laboratory" },
                      });
                    }}
                    color={colors.primary}
                    display={"block"}
                    sx={{
                      textDecoration: "none",
                      paddingX: "5px",
                      cursor: "pointer",
                    }}
                  >
                    'User List'
                  </Typography>
                  {"section."}
                </Typography>
              </Box>
            </Box>
            <Typography className={style.laboratoryDetailHeading}>
              Report Delivery (Medical Records Department) Details
            </Typography>
            <Box>
              <Box className={style.detailsRow}>
                <Typography className={style.title}>Email</Typography>
                <Typography className={style.value}>
                  {laboratoryDetails?.address?.mail}
                </Typography>
              </Box>
              <Box className={style.detailsRow}>
                <Typography className={style.title}>Fax Number</Typography>
                <Typography className={style.value}>
                  {laboratoryDetails?.address?.fax}
                </Typography>
              </Box>
            </Box>
            <Typography className={style.laboratoryDetailHeading}>
              Mode of Delivery
            </Typography>
            <Box>
              <Box className={style.detailsRow}>
                <Typography className={style.title}>Email</Typography>
                <Checkbox
                  checked={deliveryEmailSelected}
                  disableTouchRipple
                  sx={{
                    transform: "scale(0.92)",
                    padding: "0px",
                  }}
                  disabled
                />
              </Box>
              <Box className={style.detailsRow}>
                <Typography className={style.title}>Fax Number</Typography>
                <Checkbox
                  checked={faxNumberSelected}
                  disableTouchRipple
                  sx={{
                    transform: "scale(0.92)",
                    padding: "0px",
                    color: colors.primary,
                  }}
                  disabled
                />
              </Box>
            </Box>
            {laboratoryDetails?.deactivate_reason && (
              <Box>
                <Typography className={style.laboratoryDetailHeading}>
                  Deactivation Details
                </Typography>
                <Box>
                  <Box className={style.detailsRow}>
                    <Typography className={style.title}>
                      Reason for deactivation
                    </Typography>
                    <Typography className={style.value}>
                      {laboratoryDetails?.deactivate_reason}
                    </Typography>
                  </Box>
                  <Box className={style.detailsRow}>
                    <Typography className={style.title}>
                      Date of Deactivation
                    </Typography>
                    <Typography className={style.value}>
                      {laboratoryDetails?.deactivated_at
                        ? dayjs(laboratoryDetails?.deactivated_at).format(
                          "MMMM DD YYYY, hh:mm:ss A"
                        )
                        : "-"}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            )}
            {message && (
              <Box mt={2}>
                <LabelBox
                  boxProps={{
                    height: 50,
                    display: "flex",
                    alignItems: "center",
                  }}
                  data={message}
                  onClose={() => setMessage(null)}
                />
              </Box>
            )}
            <Box className={style.buttons} sx={{ marginBottom: "20px" }}>
              <Button
                sx={{
                  width: "200px",
                  height: "55px",
                  ":hover": { backgroundColor: "transparent" },
                }}
                variant="outlined"
                onClick={() => navigate(-1)}
                label={"Close"}
              />
              {laboratoryDetails?.is_active ? (
                <Button
                  sx={{
                    width: "200px",
                    height: "55px",
                    ":hover": { backgroundColor: colors.primary },
                  }}
                  variant="contained"
                  label={"Edit"}
                  onClick={() =>
                    navigate(`/laboratories/edit/${laboratoryDetails?.id}`)
                  }
                />
              ) : (
                <Button
                  sx={{
                    width: "200px",
                    height: "55px",
                    ":hover": { backgroundColor: colors.primary },
                  }}
                  variant="contained"
                  label={"Activate"}
                  onClick={() => setActivateModalVisible(true)}
                />
              )}
            </Box>
          </Box>
        )}
      </Box>
      {activateModalVisible && (
        <ConfirmationDialog
          visible={activateModalVisible}
          message={`Are you sure you want to activate "${laboratoryDetails?.name}"?`}
          onClose={() => setActivateModalVisible(false)}
          onConfirm={handleActivate}
          loading={isActivating}
        />
      )}
    </Box>
  );
};

export default LaboratoryDetails;
